import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getActiveAlerts, AlertSettings } from "@/lib/alerts";

// Module-level flag: survives component remounts, resets on page reload
let fttAutoReconnectDone = false;

export const adminLoginSchema = z.object({
    username: z.string().min(1, "Username is required"),
    password: z.string().min(1, "Password is required"),
});

export type AdminLoginData = z.infer<typeof adminLoginSchema>;

export interface Player {
    id: string;
    name: string;
    eloRating: number;
    matchesWon: number;
    matchesLost: number;
    matchesPlayed: number;
    isPremium: boolean;
    premiumType?: string | null;
    premiumEndDate?: Date | string | null;
    userId?: string | null;
    user?: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        isVerified: boolean;
        isPhoneVerified: boolean;
        isSuspended: boolean;
        suspendedUntil?: Date | null;

        suspensionReason?: string | null;
        acceptsMarketingEmails?: boolean;
        lastLoginAt?: Date | string | null;
        language?: string;
    } | null;
    statistics?: any;
    assimilatedElo?: number | null;
    fttRank?: number | null;
}

export function useAdmin() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);
    const [suspensionDialogOpen, setSuspensionDialogOpen] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
    const [premiumType, setPremiumType] = useState<string>("");
    const [premiumEndDate, setPremiumEndDate] = useState<string>("");
    const [suspensionReason, setSuspensionReason] = useState<string>("");
    const [suspendedUntil, setSuspendedUntil] = useState<string>("");

    const [emailFilter, setEmailFilter] = useState<string>("all");
    const [suspendedFilter, setSuspendedFilter] = useState<string>("all");
    const [premiumFilter, setPremiumFilter] = useState<string>("all");
    const [statsFilter, setStatsFilter] = useState<string>("all");
    const [fttSearchQuery, setFttSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [fttSyncLogs, setFttSyncLogs] = useState<{ message: string, isError?: boolean, timestamp: string }[]>([]);
    const [isSyncingFtt, setIsSyncingFtt] = useState(false);
    const [fttOffset, setFttOffset] = useState(0);
    const [allFttResults, setAllFttResults] = useState<any[]>([]);
    const [hasMoreFtt, setHasMoreFtt] = useState(false);
    const [statsPeriod, setStatsPeriod] = useState(30);

    // Deleted players restore dialog state
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [selectedDeletedPlayer, setSelectedDeletedPlayer] = useState<any | null>(null);
    const [restoreUserId, setRestoreUserId] = useState("");
    const [restoreName, setRestoreName] = useState("");

    // ELO Modification dialog state
    const [eloDialogOpen, setEloDialogOpen] = useState(false);
    const [newEloRating, setNewEloRating] = useState<number | "">("");

    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Debounce search query
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(fttSearchQuery);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [fttSearchQuery]);

    const adminForm = useForm<AdminLoginData>({
        resolver: zodResolver(adminLoginSchema),
        defaultValues: { username: "", password: "" },
    });

    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
                const token = localStorage.getItem("authToken");
                const isAdmin = localStorage.getItem("isAdmin");
                if (token && isAdmin === "true") {
                    setIsAuthenticated(true);
                }
            }
        } catch (error) {
            console.error("Error checking admin access:", error);
        }
    }, []);

    const adminLoginMutation = useMutation({
        mutationFn: async (data: AdminLoginData) => {
            const response = await apiRequest("POST", "/api/auth/admin-login", data);
            return response.json();
        },
        onSuccess: (data) => {
            if (data.token && data.isAdmin) {
                try {
                    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
                        localStorage.setItem("authToken", data.token);
                        localStorage.setItem("isAdmin", "true");
                    }
                } catch (error) {
                    console.error("Error saving admin token:", error);
                }
                setIsAuthenticated(true);
                toast({ description: "Admin login successful!" });
            } else {
                toast({
                    description: "Admin login failed",
                    variant: "destructive",
                });
            }
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Admin login failed",
                variant: "destructive",
            });
        },
    });

    const { data: players = [], isLoading, refetch } = useQuery<Player[]>({
        // Chargé une seule fois ; la recherche est filtrée localement (filteredPlayers)
        queryKey: ["/api/admin/players"],
        queryFn: async () => {
            const response = await apiRequest("GET", "/api/admin/players");
            return response.json();
        },
        enabled: isAuthenticated,
    });

    const { data: alertSettings, isLoading: isLoadingAlerts } = useQuery({
        queryKey: ["/api/admin/alerts"],
        queryFn: async () => {
            const response = await apiRequest("GET", "/api/admin/alerts");
            return response.json();
        },
        enabled: isAuthenticated,
    });

    const { data: adminClubs = [], isLoading: isLoadingClubs, refetch: refetchClubs } = useQuery({
        queryKey: ["/api/admin/clubs"],
        queryFn: async () => {
            const response = await apiRequest("GET", "/api/admin/clubs");
            return response.json();
        },
        enabled: isAuthenticated,
    });

    const { data: deletedPlayers = [], isLoading: isLoadingDeletedPlayers, refetch: refetchDeletedPlayers } = useQuery<any[]>({
        queryKey: ["/api/admin/deleted-players"],
        queryFn: async () => {
            const response = await apiRequest("GET", "/api/admin/deleted-players");
            return response.json();
        },
        enabled: isAuthenticated,
    });

    const { data: adminStats, isLoading: isLoadingStats, refetch: refetchStats } = useQuery({
        queryKey: ["/api/admin/stats", statsPeriod],
        queryFn: async () => {
            const response = await apiRequest("GET", `/api/admin/stats?period=${statsPeriod}`);
            return response.json();
        },
        enabled: isAuthenticated,
        staleTime: 1000 * 60 * 60, // 1h cache, manual refresh
    });

    const { data: websiteStats, isLoading: isLoadingWebsiteStats, refetch: refetchWebsiteStats } = useQuery({
        queryKey: ["/api/admin/website-stats", statsPeriod],
        queryFn: async () => {
            const response = await apiRequest("GET", `/api/admin/website-stats?period=${statsPeriod}`);
            return response.json();
        },
        enabled: isAuthenticated,
        staleTime: 1000 * 60 * 60, // 1h cache, manual refresh
    });

    const { data: fttStatus, isLoading: isLoadingFttStatus } = useQuery({
        queryKey: ["/api/admin/ftt/status"],
        queryFn: async () => {
            const response = await apiRequest("GET", "/api/admin/ftt/status");
            return response.json();
        },
        enabled: isAuthenticated,
        refetchInterval: (query: any) => {
            // Poll faster if syncing to keep metadata fresh, otherwise every minute
            return query?.state?.data?.syncState?.isSyncing ? 5000 : 60000;
        }
    });

    // Auto-reconnect to sync if page refreshed and sync is ongoing on server (only once per session)
    useEffect(() => {
        if (isAuthenticated && fttStatus?.syncState?.isSyncing && !fttAutoReconnectDone) {
            fttAutoReconnectDone = true;
            console.log("Auto-reconnecting to ongoing FTT sync...");
            startFttSync(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, fttStatus?.syncState?.isSyncing]);

    // Reset results when search query changes
    useEffect(() => {
        setAllFttResults([]);
        setFttOffset(0);
        setHasMoreFtt(false);
    }, [debouncedSearchQuery]);

    const { data: fttQueryData, isLoading: isSearchingFtt } = useQuery({
        queryKey: ["/api/admin/ftt/search", debouncedSearchQuery, fttOffset],
        queryFn: async () => {
            if (debouncedSearchQuery.length < 2) return { results: [], hasMore: false };
            const response = await apiRequest("GET", `/api/admin/ftt/search?q=${encodeURIComponent(debouncedSearchQuery)}&offset=${fttOffset}&limit=10`);
            return response.json();
        },
        enabled: isAuthenticated && debouncedSearchQuery.length >= 2,
    });

    // Accumulate results
    useEffect(() => {
        if (fttQueryData?.results) {
            if (fttOffset === 0) {
                setAllFttResults(fttQueryData.results);
            } else {
                setAllFttResults(prev => [...prev, ...fttQueryData.results]);
            }
            setHasMoreFtt(fttQueryData.hasMore);
        }
    }, [fttQueryData, fttOffset]);

    const loadMoreFttResults = () => {
        if (!isSearchingFtt && hasMoreFtt) {
            setFttOffset(prev => prev + 10);
        }
    };

    const startFttSync = (isReconnect = false) => {
        if (isSyncingFtt) return;

        if (!isReconnect) {
            setFttSyncLogs([]);
        }
        setIsSyncingFtt(true);

        const token = localStorage.getItem("authToken");
        const eventSource = new EventSource(`/api/admin/ftt/sync/stream?token=${encodeURIComponent(token || "")}`);

        eventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.type === 'log') {
                setFttSyncLogs(prev => {
                    // Avoid duplicate logs during reconnection
                    const timeStr = new Date().toLocaleTimeString();
                    const newLog = {
                        message: data.message,
                        isError: data.isError,
                        timestamp: data.timestamp || timeStr
                    };

                    if (prev.some(l => l.message === newLog.message && l.timestamp === newLog.timestamp)) {
                        return prev;
                    }
                    return [...prev, newLog];
                });
            } else if (data.type === 'complete') {
                setIsSyncingFtt(false);
                eventSource.close();
                queryClient.invalidateQueries({ queryKey: ["/api/admin/ftt/status"] });
                toast({
                    title: "FFT Sync Complete",
                    description: data.message,
                });
            } else if (data.type === 'error') {
                setIsSyncingFtt(false);
                eventSource.close();
                toast({
                    title: "FFT Sync Error",
                    description: data.message,
                    variant: "destructive",
                });
            }
        };

        eventSource.onerror = (error) => {
            console.error("SSE Error:", error);
            setIsSyncingFtt(false);
            eventSource.close();
            toast({
                title: "Connection Lost",
                description: "The connection to the sync server was lost.",
                variant: "destructive",
            });
        };
    };

    const searchLower = searchQuery.trim().toLowerCase();
    const filteredPlayers = players.filter((player) => {
        if (searchLower && !(
            player.name.toLowerCase().includes(searchLower) ||
            player.user?.email?.toLowerCase().includes(searchLower) ||
            player.user?.firstName?.toLowerCase().includes(searchLower) ||
            player.user?.lastName?.toLowerCase().includes(searchLower)
        )) return false;

        // Email status filter
        if (emailFilter === "verified" && !player.user?.isVerified) return false;
        if (emailFilter === "not-verified" && player.user?.isVerified) return false;

        // Suspended status filter
        if (suspendedFilter === "suspended" && !player.user?.isSuspended) return false;
        if (suspendedFilter === "not-suspended" && player.user?.isSuspended) return false;

        // Premium status filter
        if (premiumFilter === "premium" && !player.isPremium) return false;
        if (premiumFilter === "not-premium" && player.isPremium) return false;

        // Stats alert filter (requires alertSettings)
        if (statsFilter === "alert" && alertSettings) {
            const alerts = getActiveAlerts(player.statistics, alertSettings);
            if (alerts?.length === 0) return false;
        }

        return true;
    });

    const activateAccountMutation = useMutation({
        mutationFn: async (userId: string) => {
            const response = await apiRequest("POST", `/api/admin/accounts/${userId}/activate`);
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Account activated successfully!" });
            refetch();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to activate account",
                variant: "destructive",
            });
        },
    });

    const updatePremiumMutation = useMutation({
        mutationFn: async (data: { playerId: string; isPremium: boolean; premiumType?: string | null; premiumEndDate?: string | null }) => {
            const response = await apiRequest("POST", `/api/admin/players/${data.playerId}/premium`, {
                isPremium: data.isPremium,
                premiumType: data.premiumType || null,
                premiumStartDate: data.isPremium ? new Date().toISOString() : null,
                premiumEndDate: data.premiumEndDate || null,
            });
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Premium status updated successfully!" });
            setPremiumDialogOpen(false);
            setSelectedPlayer(null);
            refetch();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to update premium status",
                variant: "destructive",
            });
        },
    });

    const suspendAccountMutation = useMutation({
        mutationFn: async (data: { userId: string; reason: string; suspendedUntil?: string | null }) => {
            const response = await apiRequest("POST", `/api/admin/accounts/${data.userId}/suspend`, {
                reason: data.reason,
                suspendedUntil: data.suspendedUntil || null,
            });
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Account suspended successfully!" });
            setSuspensionDialogOpen(false);
            setSelectedPlayer(null);
            setSuspensionReason("");
            setSuspendedUntil("");
            refetch();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to suspend account",
                variant: "destructive",
            });
        },
    });

    const updateEloMutation = useMutation({
        mutationFn: async (data: { playerId: string; eloRating: number }) => {
            const response = await apiRequest("PATCH", `/api/admin/players/${data.playerId}/elo`, {
                eloRating: data.eloRating,
            });
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Player ELO updated successfully!" });
            setEloDialogOpen(false);
            setSelectedPlayer(null);
            setNewEloRating("");
            refetch();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to update player ELO",
                variant: "destructive",
            });
        },
    });



    const updateAlertSettingsMutation = useMutation({
        mutationFn: async (data: any) => {
            const response = await apiRequest("PUT", "/api/admin/alerts", data);
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Alert settings updated successfully!" });
            queryClient.invalidateQueries({ queryKey: ["/api/admin/alerts"] });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to update alert settings",
                variant: "destructive",
            });
        }
    });

    const unsuspendAccountMutation = useMutation({
        mutationFn: async (userId: string) => {
            const response = await apiRequest("POST", `/api/admin/accounts/${userId}/unsuspend`);
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Account unsuspended successfully!" });
            refetch();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to unsuspend account",
                variant: "destructive",
            });
        },
    });

    const deleteAccountMutation = useMutation({
        mutationFn: async (accountId: string) => {
            await apiRequest("DELETE", `/api/admin/accounts/${accountId}`);
        },
        onSuccess: () => {
            toast({ description: "Account deleted successfully!" });
            refetch();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to delete account",
                variant: "destructive",
            });
        },
    });

    const triggerRecapSnapshotMutation = useMutation({
        mutationFn: async (date?: string) => {
            const response = await apiRequest("POST", "/api/admin/monthly-recap/snapshot", { date });
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Snapshot generation triggered successfully!" });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to trigger snapshot generation",
                variant: "destructive",
            });
        },
    });

    const triggerRecapEmailMutation = useMutation({
        mutationFn: async (data?: { date?: string; userIds?: string[] }) => {
            const response = await apiRequest("POST", "/api/admin/monthly-recap/email", data || {});
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Email processing triggered successfully!" });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to trigger email processing",
                variant: "destructive",
            });
        },
    });

    const sendBroadcastEmailMutation = useMutation({
        mutationFn: async (data: { subject: string; message: string; userIds?: string[] }) => {
            const response = await apiRequest("POST", "/api/admin/communications/broadcast", data);
            return response.json();
        },
        onSuccess: (data) => {
            toast({
                description: `Broadcast sent successfully! Recipient count: ${data.details?.recipientCount || 0}`,
                duration: 5000
            });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to send broadcast email",
                variant: "destructive",
            });
        },
    });

    const updatePreferencesMutation = useMutation({
        mutationFn: async (data: { userId: string; acceptsMarketingEmails: boolean }) => {
            const response = await apiRequest("POST", `/api/admin/accounts/${data.userId}/preferences`, {
                acceptsMarketingEmails: data.acceptsMarketingEmails
            });
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "User preferences updated successfully!" });
            refetch();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to update user preferences",
                variant: "destructive",
            });
        }
    });

    const updateClubVerificationMutation = useMutation({
        mutationFn: async (data: { clubId: string; isVerified: boolean }) => {
            const response = await apiRequest("PATCH", `/api/admin/clubs/${data.clubId}/verify`, {
                isVerified: data.isVerified
            });
            return response.json();
        },
        onSuccess: (data) => {
            toast({ description: data.message || "Club verification status updated successfully!" });
            refetchClubs();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to update club verification",
                variant: "destructive",
            });
        }
    });

    const deleteClubAdminMutation = useMutation({
        mutationFn: async (clubId: string) => {
            const response = await apiRequest("DELETE", `/api/admin/clubs/${clubId}`);
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Club supprimé avec succès !" });
            refetchClubs();
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Échec de la suppression du club",
                variant: "destructive",
            });
        }
    });

    const sendSeasonRecapMutation = useMutation({
        mutationFn: async (data: { clubId: string; seasonId: string; seasonStart: string; seasonEnd: string }) => {
            const response = await apiRequest("POST", `/api/clubs/${data.clubId}/send-season-recap`, {
                seasonId: data.seasonId,
                seasonStart: data.seasonStart,
                seasonEnd: data.seasonEnd
            });
            return response.json();
        },
        onSuccess: (data) => {
            toast({ description: data.message || "Season recap emails sent successfully!" });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to send season recap emails",
                variant: "destructive",
            });
        }
    });

    const { data: fttSettings } = useQuery({
        queryKey: ["/api/admin/ftt/settings"],
        queryFn: async () => {
            const response = await apiRequest("GET", "/api/admin/ftt/settings");
            return response.json() as Promise<{ autoSyncEnabled: boolean }>;
        },
        enabled: isAuthenticated,
    });

    const updateFttSettingsMutation = useMutation({
        mutationFn: async (data: { autoSyncEnabled: boolean }) => {
            const response = await apiRequest("POST", "/api/admin/ftt/settings", data);
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "FFT settings updated successfully!" });
            queryClient.invalidateQueries({ queryKey: ["/api/admin/ftt/status"] });
            queryClient.invalidateQueries({ queryKey: ["/api/admin/ftt/settings"] });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to update FFT settings",
                variant: "destructive",
            });
        },
    });

    const deleteUserDelMutation = useMutation({
        mutationFn: async (playerId: string) => {
            const response = await apiRequest("DELETE", `/api/admin/deleted-players/${playerId}`);
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Joueur UserDEL supprimé définitivement." });
            refetchDeletedPlayers();
            queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Échec de la suppression",
                variant: "destructive",
            });
        },
    });

    const restoreDeletedPlayerMutation = useMutation({
        mutationFn: async (data: { playerId: string; userId: string; name: string }) => {
            const response = await apiRequest("POST", `/api/admin/deleted-players/${data.playerId}/restore`, {
                userId: data.userId,
                name: data.name,
            });
            return response.json();
        },
        onSuccess: () => {
            toast({ description: "Joueur restauré avec succès !" });
            setRestoreDialogOpen(false);
            setSelectedDeletedPlayer(null);
            setRestoreUserId("");
            setRestoreName("");
            refetchDeletedPlayers();
            refetch();
            queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Échec de la restauration",
                variant: "destructive",
            });
        },
    });

    const resetFttSyncMutation = useMutation({
        mutationFn: async () => {
            const response = await apiRequest("POST", "/api/admin/ftt/sync/reset");
            return response.json();
        },
        onSuccess: () => {
            fttAutoReconnectDone = false;
            queryClient.invalidateQueries({ queryKey: ["/api/admin/ftt/status"] });
            toast({ title: "Sync state reset", description: "FTT sync state has been reset." });
        },
        onError: (error: any) => {
            toast({ description: error.message || "Failed to reset sync state", variant: "destructive" });
        },
    });

    const syncPlayersWithFttMutation = useMutation({
        mutationFn: async () => {
            const response = await apiRequest("POST", "/api/admin/ftt/sync-players");
            return response.json();
        },
        onSuccess: (data) => {
            toast({ 
                title: "Player Sync Complete",
                description: `Synced ${data.synced} players. ${data.notFound} not found in FFT database.`
            });
            queryClient.invalidateQueries({ queryKey: ["/api/admin/players"] });
        },
        onError: (error: any) => {
            toast({
                description: error.message || "Failed to sync players with FFT",
                variant: "destructive",
            });
        },
    });

    const handleOpenPremiumDialog = (player: Player) => {

        setSelectedPlayer(player);
        setPremiumType(player.isPremium ? (player.premiumType || "") : "");
        if (player.premiumEndDate) {
            const date = new Date(player.premiumEndDate);
            setPremiumEndDate(date.toISOString().split('T')[0]);
        } else {
            setPremiumEndDate("");
        }
        setPremiumDialogOpen(true);
    };

    const handleOpenSuspensionDialog = (player: Player) => {
        setSelectedPlayer(player);
        setSuspensionReason(player.user?.suspensionReason || "");
        if (player.user?.suspendedUntil) {
            const date = new Date(player.user.suspendedUntil);
            setSuspendedUntil(date.toISOString().split('T')[0]);
        } else {
            setSuspendedUntil("");
        }
        setSuspensionDialogOpen(true);
    };

    const handleOpenEloDialog = (player: Player) => {
        setSelectedPlayer(player);
        setNewEloRating(player.eloRating);
        setEloDialogOpen(true);
    };

    const handleSuspendAccount = () => {
        if (!selectedPlayer || !selectedPlayer.user?.id) return;

        suspendAccountMutation.mutate({
            userId: selectedPlayer.user.id,
            reason: suspensionReason || "Account suspended by administrator",
            suspendedUntil: suspendedUntil || null,
        });
    };

    const handleUpdatePremium = () => {
        if (!selectedPlayer) return;

        const isPremium = premiumType !== "" && premiumType !== "none";
        updatePremiumMutation.mutate({
            playerId: selectedPlayer.id,
            isPremium,
            premiumType: isPremium ? premiumType : null,
            premiumEndDate: isPremium && premiumEndDate ? premiumEndDate : null,
        });
    };

    const calculateEndDate = (type: string): string => {
        if (type === "none" || !type) return "";
        const today = new Date();
        if (type === "monthly") {
            today.setMonth(today.getMonth() + 1);
        } else if (type === "yearly") {
            today.setFullYear(today.getFullYear() + 1);
        } else if (type === "lifetime") {
            // Set to 100 years from now (effectively lifetime)
            today.setFullYear(today.getFullYear() + 100);
        }
        return today.toISOString().split('T')[0];
    };

    const handleAdminLogin = adminForm.handleSubmit((data) => {
        adminLoginMutation.mutate(data);
    });

    const handleLogout = () => {
        try {
            if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
                localStorage.removeItem("authToken");
                localStorage.removeItem("isAdmin");
            }
        } catch (error) {
            console.error("Error during logout:", error);
        }
        setIsAuthenticated(false);
        adminForm.reset();
    };

    return {
        isAuthenticated,
        setIsAuthenticated,
        searchQuery,
        setSearchQuery,
        premiumDialogOpen,
        setPremiumDialogOpen,
        suspensionDialogOpen,
        setSuspensionDialogOpen,
        selectedPlayer,
        setSelectedPlayer,
        premiumType,
        setPremiumType,
        premiumEndDate,
        setPremiumEndDate,
        suspensionReason,
        setSuspensionReason,
        suspendedUntil,
        setSuspendedUntil,
        emailFilter,
        setEmailFilter,
        suspendedFilter,
        setSuspendedFilter,
        premiumFilter,
        setPremiumFilter,
        statsFilter,
        setStatsFilter,
        players,
        filteredPlayers,
        isLoading,
        adminForm,
        adminLoginMutation,
        activateAccountMutation,
        updatePremiumMutation,
        suspendAccountMutation,
        unsuspendAccountMutation,
        deleteAccountMutation,
        alertSettings,
        isLoadingAlerts,
        updateAlertSettingsMutation,
        handleOpenPremiumDialog,
        handleOpenSuspensionDialog,
        handleSuspendAccount,
        handleUpdatePremium,
        calculateEndDate,
        handleAdminLogin,
        handleLogout,
        triggerRecapSnapshotMutation,
        triggerRecapEmailMutation,
        sendBroadcastEmailMutation,
        updatePreferencesMutation,
        // ELO state and mutators
        eloDialogOpen,
        setEloDialogOpen,
        newEloRating,
        setNewEloRating,
        handleOpenEloDialog,
        updateEloMutation,
        // Clubs
        adminClubs,
        isLoadingClubs,
        updateClubVerificationMutation,
        deleteClubAdminMutation,
        sendSeasonRecapMutation,
        // FFT
        fttSearchQuery,
        setFttSearchQuery,
        fttStatus,
        isLoadingFttStatus,
        fttSearchResults: allFttResults,
        hasMoreFtt,
        loadMoreFttResults,
        isSearchingFtt,
        fttSyncLogs,
        isSyncingFtt,
        startFttSync,
        resetFttSyncMutation,
        syncPlayersWithFttMutation,
        // FFT Settings
        fttSettings,
        updateFttSettingsMutation,
        // Dashboard Stats
        adminStats,
        isLoadingStats,
        refetchStats,
        statsPeriod,
        setStatsPeriod,
        // Website Stats
        websiteStats,
        isLoadingWebsiteStats,
        refetchWebsiteStats,
        // Deleted players
        deletedPlayers,
        isLoadingDeletedPlayers,
        refetchDeletedPlayers,
        restoreDialogOpen,
        setRestoreDialogOpen,
        selectedDeletedPlayer,
        setSelectedDeletedPlayer,
        restoreUserId,
        setRestoreUserId,
        restoreName,
        setRestoreName,
        restoreDeletedPlayerMutation,
        deleteUserDelMutation,
    };
}
